import { useEffect } from "react";

import * as Sentry from "@sentry/react";

const PIXEL_ID = "t2_1b38rs29";

declare global {
  interface Window {
    rdt?: (...args: unknown[]) => void;
    amplitude?: {
      getInstance: () => {
        options: {
          deviceId?: string;
        };
      };
    };
  }
}

export type RedditPixelEvent = {
  externalId?: string;
  email?: string;
};

const getAmplitudeDeviceId = () => {
  return window?.amplitude?.getInstance()?.options?.deviceId;
};

const initReddit = (pixelId: string) => {
  try {
    window?.rdt?.("init", pixelId, { externalId: getAmplitudeDeviceId() });
  } catch (error) {
    console.error("[Reddit Pixel] Initialization error:", error);
    Sentry.captureException(error, {
      level: "error",
      tags: { component: "reddit_pixel", action: "initialize" },
    });
  }
};

export const useRedditPixel = (pixelId = PIXEL_ID) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: the pixel is initialised exactly once per mount; listing pixelId would re-initialise the third-party pixel whenever the prop identity changed and double-count visits.
  useEffect(() => {
    initReddit(pixelId);
  }, []);

  const redditTrackSignUp = (eventData: RedditPixelEvent) => {
    try {
      window?.rdt?.("track", "SignUp", {
        externalId: getAmplitudeDeviceId(),
        ...eventData,
      });
    } catch (error) {
      console.error("[Reddit Pixel] Sign up tracking error:", error);
      Sentry.captureException(error, {
        level: "error",
        tags: { component: "reddit_pixel", action: "sign_up" },
      });
    }
  };

  const redditTrackPageVisited = (eventData: RedditPixelEvent) => {
    try {
      window?.rdt?.("track", "PageVisit", {
        externalId: getAmplitudeDeviceId(),
        ...eventData,
      });
    } catch (error) {
      console.error("[Reddit Pixel] Page visited tracking error:", error);
      Sentry.captureException(error, {
        level: "error",
        tags: { component: "reddit_pixel", action: "page_visited" },
      });
    }
  };

  const redditTrackCustomEvent = (
    eventName: string,
    eventData: RedditPixelEvent,
  ) => {
    try {
      window?.rdt?.("track", eventName, {
        externalId: getAmplitudeDeviceId(),
        ...eventData,
      });
    } catch (error) {
      console.error("[Reddit Pixel] Custom event tracking error:", error);
      Sentry.captureException(error, {
        level: "error",
        tags: { component: "reddit_pixel", action: "custom_event" },
      });
    }
  };

  return {
    redditTrackSignUp,
    redditTrackPageVisited,
    redditTrackCustomEvent,
  };
};
